import {
  Box,
  Heading,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import LoadingSpinner from "../../../LoadingSpinner";

// Helper to format date values
const formatDate = (value) => {
  if (!value) return "—";

  if (!isNaN(Number(value)) && value.toString().length > 8) {
    const date = new Date(Number(value));
    return isNaN(date.getTime()) ? value : date.toISOString().split("T")[0];
  }

  if (typeof value === "string" && /^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }

  return value;
};

// Render array fields as table
const renderArrayField = (arrayField = []) => {
  if (!Array.isArray(arrayField) || arrayField.length === 0) {
    return <Text>—</Text>;
  }

  const firstItem = arrayField[0] || {};

  const headers = firstItem?.PREVIEW?.PREVIEW_VALUE
    ? firstItem.PREVIEW.PREVIEW_VALUE
    : Object.keys(firstItem).filter((k) => k !== "PREVIEW");

  if (!headers.length) return <Text>—</Text>;

  return (
    <TableContainer
      mt={2}
      mb={2}
      overflowX="auto"
      maxW="100%"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      <Table variant="simple" size="sm" minW="max-content">
        <Thead>
          <Tr>
            {headers.map((h) => (
              <Th key={h} whiteSpace="nowrap">
                {String(h).replace(/_/g, " ")}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {arrayField.map((item = {}, idx) => (
            <Tr key={idx}>
              {headers.map((h) => {
                const val = item?.[h]?.VALUE ?? "—";

                return (
                  <Td key={`${idx}-${h}`} fontSize="xs" whiteSpace="nowrap">
                    {String(h).toUpperCase().includes("DATE")
                      ? formatDate(val)
                      : String(val)}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const renderFieldValue = (field) => {
  if (!field) return "—";

  if (Array.isArray(field)) return renderArrayField(field);

  if (typeof field === "object") {
    const val = field?.VALUE ?? "—";

    // ✅ Handle case where VALUE itself is an array
    if (Array.isArray(val)) {
      return renderArrayField(val);
    }

    if (
      field?.TYPE === "Date" ||
      field?.MESSAGE?.toLowerCase?.().includes("date")
    ) {
      return formatDate(val);
    }

    return String(val);
  }

  return String(field);
};

// MAIN COMPONENT
const DisplayCaseInformation = ({ data = {}, isLoading = false }) => {
  if (isLoading) return <LoadingSpinner />;

  // 🔥 prevents Object.keys crash
  if (!data || Object.keys(data).length === 0) {
    return (
      <Box p={6}>
        <Text fontSize="md" color="gray.500">
          No case information available.
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {Object.keys(data).map((sectionKey) => {
        const section = data?.[sectionKey];

        // 🔥 protects against missing sections
        if (!section || section?.UI_SKIP || !section?.FIELDS) return null;

        return (
          <Box key={sectionKey} p={4} borderWidth={1} borderRadius="md">
            <Heading size="sm" mb={2}>
              {section?.MESSAGE || "Section"}
            </Heading>

            <SimpleGrid columns={3} spacing={4}>
              {Object.keys(section?.FIELDS || {}).map((fieldKey) => {
                const field = section?.FIELDS?.[fieldKey];

                if (!field || field?.VISIBLE === false) return null;

                // Array fields span full width
                if (Array.isArray(field)) {
                  return (
                    <Box key={fieldKey} gridColumn="span 3">
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        fontWeight="bold"
                        mb={1}
                      >
                        {field?.[0]?.PREVIEW?.MESSAGE || fieldKey}:
                      </Text>

                      {renderArrayField(field)}
                    </Box>
                  );
                }

                return (
                  <Box key={fieldKey}>
                    <Text
                      fontSize="sm"
                      color="gray.600"
                      fontWeight="bold"
                      mb={1}
                    >
                      {field?.MESSAGE || fieldKey}:
                    </Text>

                    <Text>{renderFieldValue(field)}</Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        );
      })}
    </VStack>
  );
};

export default DisplayCaseInformation;
